/**
 * BlockNote 블록 JSON → 외부 이식용 HTML (A3).
 *
 * BlockNote의 blocksToHTMLLossy(ExternalHTMLExporter) + blocksToFullHTML 대응.
 *  - blocksToHtml(blocks): 시맨틱 HTML 조각(<h1>/<p>/<ul>/<table>/<pre> …). 색은 실제 hex,
 *    글자크기는 인라인 style, 토글은 <details>/<summary>, 표는 colspan/rowspan.
 *  - blocksToFullHtml(blocks, {title}): 위 조각을 <!doctype html> 문서로 감싸고 최소 스타일 포함.
 *
 * 라운드트립을 보장하지 않는다(외부 복사/공유용). 라운드트립은 blocks↔DOM(JSON)이 담당.
 */
import { getHexFromColorValue } from "../constants/colors.js";
import { neutralizeUrl } from "../utils/url.js";

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escAttr(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

const LIST_TYPES = new Set(["bulletListItem", "numberedListItem", "checkListItem"]);

/** styled-text 한 노드 → HTML(외→내 중첩). 색은 실제 hex, fontSize는 style. */
function styledToHtml(node) {
  let html = esc(node && node.text != null ? node.text : "").replace(/\n/g, "<br>");
  const s = (node && node.styles) || {};
  if (s.code) html = `<code>${html}</code>`;
  if (s.strike) html = `<s>${html}</s>`;
  if (s.underline) html = `<u>${html}</u>`;
  if (s.italic) html = `<em>${html}</em>`;
  if (s.bold) html = `<strong>${html}</strong>`;
  if (s.fontSize) html = `<span style="font-size:${escAttr(s.fontSize)}">${html}</span>`;
  if (s.backgroundColor && s.backgroundColor !== "default")
    html = `<span style="background-color:${escAttr(getHexFromColorValue(s.backgroundColor, "background"))}">${html}</span>`;
  if (s.textColor && s.textColor !== "default")
    html = `<span style="color:${escAttr(getHexFromColorValue(s.textColor, "text"))}">${html}</span>`;
  return html;
}

export function inlineToHtml(content) {
  if (!Array.isArray(content)) return "";
  let oln = 0; // 셀 내 순서목록 서수(best-effort — 호출당=콘텐츠 배열당 리셋)
  return content
    .map((node) => {
      if (node && node.type === "checkbox") {
        return `<input type="checkbox" disabled${node.checked ? " checked" : ""}>`;
      }
      if (node && node.type === "olItem") { oln += 1; return `${oln}. `; }
      if (node && node.type === "ulItem") return "• ";
      if (node && node.type === "link") {
        const inner = inlineToHtml(node.content || []);
        return `<a href="${escAttr(neutralizeUrl(node.href))}">${inner}</a>`;
      }
      return styledToHtml(node);
    })
    .join("");
}

function blockStyle(props) {
  const st = [];
  if (props) {
    if (props.textAlignment && props.textAlignment !== "left") st.push(`text-align:${props.textAlignment}`);
    if (props.textColor && props.textColor !== "default") st.push(`color:${getHexFromColorValue(props.textColor, "text")}`);
    if (props.backgroundColor && props.backgroundColor !== "default")
      st.push(`background-color:${getHexFromColorValue(props.backgroundColor, "background")}`);
  }
  return st.length ? ` style="${escAttr(st.join(";"))}"` : "";
}

function codeText(block) {
  return (block.content || []).map((n) => (n && n.text != null ? n.text : "")).join("");
}

/** 셀 props → style 속성(색·정렬·세로정렬). 값이 없으면 "" (무스타일 표는 기존 출력과 동일). */
function tableCellStyle(p) {
  const st = [];
  if (p.backgroundColor && p.backgroundColor !== "default")
    st.push(`background-color:${getHexFromColorValue(p.backgroundColor, "background")}`);
  if (p.textColor && p.textColor !== "default") st.push(`color:${getHexFromColorValue(p.textColor, "text")}`);
  if (p.textAlignment && p.textAlignment !== "left") st.push(`text-align:${p.textAlignment}`);
  if (p.verticalAlignment && p.verticalAlignment !== "top") st.push(`vertical-align:${p.verticalAlignment}`);
  return st.length ? ` style="${escAttr(st.join(";"))}"` : "";
}

// 표 → HTML(무손실 지향): colgroup 폭, 셀 색/정렬/세로정렬, 행 높이, 머리행/열(thead/th scope),
// 표 정렬(margin). 스타일/폭/머리가 전혀 없으면 `<table><tbody>…</tbody></table>`로 degrade(붙여넣기 파서와 대칭).
function tableToHtml(block) {
  const tc = block.content;
  if (!tc || !Array.isArray(tc.rows)) return "";
  const headerRows = tc.headerRows || 0;
  const headerCols = tc.headerCols || 0;
  const cw = Array.isArray(tc.columnWidths) ? tc.columnWidths : [];
  const colgroup = cw.some((w) => w)
    ? `<colgroup>${cw.map((w) => (w ? `<col style="width:${parseInt(w, 10)}px">` : "<col>")).join("")}</colgroup>`
    : "";

  const renderRow = (r, rowIdx) => {
    const cells = (r.cells || [])
      .map((c, cellIdx) => {
        const p = (c && c.props) || {};
        const span =
          (p.colspan && p.colspan > 1 ? ` colspan="${p.colspan}"` : "") +
          (p.rowspan && p.rowspan > 1 ? ` rowspan="${p.rowspan}"` : "");
        const isHeaderRow = rowIdx < headerRows;
        const isHeaderCol = cellIdx < headerCols;
        const tag = isHeaderRow || isHeaderCol ? "th" : "td";
        const scope = isHeaderRow ? ' scope="col"' : isHeaderCol ? ' scope="row"' : "";
        return `<${tag}${scope}${span}${tableCellStyle(p)}>${inlineToHtml((c && c.content) || [])}</${tag}>`;
      })
      .join("");
    const rh = (r.cells || []).reduce((h, c) => Math.max(h, (c && c.props && c.props.rowHeight) || 0), 0);
    const trStyle = rh > 0 ? ` style="height:${parseInt(rh, 10)}px"` : "";
    return `<tr${trStyle}>${cells}</tr>`;
  };

  const rowsHtml = tc.rows.map(renderRow);
  let body;
  if (headerRows > 0) {
    const rest = rowsHtml.slice(headerRows).join("");
    body = `<thead>${rowsHtml.slice(0, headerRows).join("")}</thead>` + (rest ? `<tbody>${rest}</tbody>` : "");
  } else {
    body = `<tbody>${rowsHtml.join("")}</tbody>`;
  }

  const ta = block.props && block.props.tableAlignment;
  const tstyle =
    ta === "center" ? ' style="margin-left:auto;margin-right:auto"' : ta === "right" ? ' style="margin-left:auto"' : "";
  return `<table${tstyle}>${colgroup}${body}</table>`;
}

function renderListItem(block) {
  let inner = inlineToHtml(block.content || []);
  if (block.type === "checkListItem") {
    const checked = block.props && block.props.checked ? " checked" : "";
    inner = `<input type="checkbox" disabled${checked}> ${inner}`;
  }
  const kids = Array.isArray(block.children) && block.children.length ? renderBlocks(block.children) : "";
  return `<li>${inner}${kids}</li>`;
}

// 토글(heading isToggle / toggleListItem) → <details><summary>제목</summary>본문</details>
function toggleToHtml(block, summaryHtml) {
  const kids = Array.isArray(block.children) && block.children.length ? renderBlocks(block.children) : "";
  return `<details open><summary>${summaryHtml}</summary>${kids}</details>`;
}

function renderBlock(block) {
  const type = block.type;
  const p = block.props || {};
  const inline = () => inlineToHtml(block.content || []);

  switch (type) {
    case "heading": {
      const level = Math.min(6, Math.max(1, p.level || 1));
      const h = `<h${level}${blockStyle(p)}>${inline()}</h${level}>`;
      return p.isToggleable ? toggleToHtml(block, h) : h;
    }
    case "quote":
      return `<blockquote${blockStyle(p)}>${inline()}</blockquote>`;
    case "codeBlock": {
      const cls = p.language ? ` class="language-${escAttr(p.language)}"` : "";
      return `<pre><code${cls}>${esc(codeText(block))}</code></pre>`;
    }
    case "divider":
    case "pageBreak":
      return "<hr>";
    case "image": {
      const img = `<img src="${escAttr(neutralizeUrl(p.url))}" alt="${escAttr(p.name || p.caption || "")}"${
        p.previewWidth ? ` width="${parseInt(p.previewWidth, 10) || ""}"` : ""
      }>`;
      return p.caption ? `<figure>${img}<figcaption>${esc(p.caption)}</figcaption></figure>` : `<figure>${img}</figure>`;
    }
    case "video":
      return `<video controls src="${escAttr(neutralizeUrl(p.url))}"></video>`;
    case "audio":
      return `<audio controls src="${escAttr(neutralizeUrl(p.url))}"></audio>`;
    case "file":
      return `<a href="${escAttr(neutralizeUrl(p.url))}">${esc(p.name || "file")}</a>`;
    case "bookmark":
      // 커스텀 블록 → 이식용 HTML 은 링크로 degrade(카드/임베드 모두).
      return `<p><a href="${escAttr(neutralizeUrl(p.url))}">${esc(p.title || p.url || "link")}</a></p>`;
    case "table":
      return tableToHtml(block);
    case "toggleListItem":
      return toggleToHtml(block, inline());
    case "columnList": {
      const cols = (block.children || [])
        .map((col) => `<div class="lumir-column">${renderBlocks(col.children || [])}</div>`)
        .join("");
      return `<div class="lumir-columns" style="display:flex;gap:16px">${cols}</div>`;
    }
    case "paragraph":
    default: {
      const body = inline();
      return `<p${blockStyle(p)}>${body || "<br>"}</p>`;
    }
  }
}

function renderBlocks(blocks) {
  if (!Array.isArray(blocks)) return "";
  let html = "";
  let i = 0;
  while (i < blocks.length) {
    const t = blocks[i].type;
    if (LIST_TYPES.has(t)) {
      const tag = t === "numberedListItem" ? "ol" : "ul";
      const cls = t === "checkListItem" ? ' class="lumir-checklist"' : "";
      let startAttr = "";
      if (t === "numberedListItem" && blocks[i].props && blocks[i].props.start && blocks[i].props.start !== 1)
        startAttr = ` start="${parseInt(blocks[i].props.start, 10)}"`;
      let j = i;
      const items = [];
      while (j < blocks.length && blocks[j].type === t) {
        items.push(renderListItem(blocks[j]));
        j++;
      }
      html += `<${tag}${cls}${startAttr}>${items.join("")}</${tag}>`;
      i = j;
    } else {
      html += renderBlock(blocks[i]);
      i++;
    }
  }
  return html;
}

/** 블록 배열 → 시맨틱 HTML 조각(외부 이식용). */
export function blocksToHtml(blocks) {
  return renderBlocks(blocks || []);
}

const DEFAULT_DOC_STYLE =
  "body{font-family:ui-sans-serif,system-ui,sans-serif;line-height:1.6;max-width:720px;margin:40px auto;padding:0 16px;color:#1f1f1f}" +
  "img{max-width:100%}table{border-collapse:collapse}td,th{border:1px solid #ddd;padding:6px 10px}" +
  "pre{background:#f6f5f1;border:1px solid #e7e5e4;border-radius:7px;padding:12px 14px;overflow:auto}" +
  "blockquote{border-left:3px solid #ddd;margin:0;padding-left:14px;color:#555}" +
  ".lumir-checklist{list-style:none;padding-left:1.2em}.lumir-columns>.lumir-column{flex:1}";

/** 블록 배열 → 단독 실행 가능한 전체 HTML 문서 문자열. */
export function blocksToFullHtml(blocks, opts) {
  opts = opts || {};
  const title = escAttr(opts.title || "Document");
  const style = opts.style != null ? opts.style : DEFAULT_DOC_STYLE;
  return (
    "<!doctype html>\n" +
    `<html lang="ko">\n<head>\n<meta charset="utf-8">\n` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">\n` +
    `<title>${title}</title>\n` +
    (style ? `<style>${style}</style>\n` : "") +
    `</head>\n<body>\n${blocksToHtml(blocks)}\n</body>\n</html>\n`
  );
}
